import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import io from 'socket.io-client';
import './Chat.css';

// Import new chat components
import MessageItem from '../components/chat/MessageItem';
import MessageInput from '../components/chat/MessageInput';
import ThreadView from '../components/chat/ThreadView';
import MessageSearch from '../components/chat/MessageSearch';
import BookmarksPanel from '../components/chat/BookmarksPanel';
import UserProfileCard from '../components/chat/UserProfileCard';
import StatusSelector from '../components/chat/StatusSelector';

const Chat = () => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Map()); // Map of userId -> username
  const [userCount, setUserCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionUsers, setMentionUsers] = useState([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [roomUsers, setRoomUsers] = useState([]);
  
  // New feature states
  const [showThreadView, setShowThreadView] = useState(false);
  const [currentThread, setCurrentThread] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showBookmarksPanel, setShowBookmarksPanel] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [showStatusSelector, setShowStatusSelector] = useState(false);
  const [userStatuses, setUserStatuses] = useState(new Map()); // userId -> status info
  const [bookmarkedMessages, setBookmarkedMessages] = useState([]);
  const [roomSearchQuery, setRoomSearchQuery] = useState('');
  const [currentUserStatus, setCurrentUserStatus] = useState(user?.chatStatus || 'online');
  const [currentUserCustomStatus, setCurrentUserCustomStatus] = useState(user?.customStatus || '');
  const [collapsedCategories, setCollapsedCategories] = useState(new Set()); // Track collapsed categories
  
  const messagesEndRef = useRef(null);
  const messagesListRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);

  // Initialize user status from AuthContext
  useEffect(() => {
    if (user) {
      console.log('Initializing user status from AuthContext:', { 
        chatStatus: user.chatStatus, 
        customStatus: user.customStatus 
      });
      setCurrentUserStatus(user.chatStatus || 'online');
      setCurrentUserCustomStatus(user.customStatus || '');
    }
  }, [user]);

  // Fetch rooms
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await fetch('/api/chat/rooms', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        if (data.success) {
          setRooms(data.data);
          // Default to Global room if available and no room selected
          if (!currentRoom) {
            const globalRoom = data.data.find(r => r.slug === 'global');
            if (globalRoom) setCurrentRoom(globalRoom);
          }
        }
      } catch (error) {
        console.error('Failed to fetch rooms:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchRooms();
    }
  }, [isAuthenticated]);

  // Socket connection
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Auto-set to online when connecting to chat (unless user explicitly set a different status)
    if (user.chatStatus === 'offline' || !user.chatStatus) {
      // Automatically set to online when user opens chat
      const autoSetOnline = async () => {
        try {
          const response = await fetch('/api/chat/rooms/status', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ 
              status: 'online', 
              customStatus: user.customStatus || '' 
            })
          });
          
          if (response.ok) {
            console.log('Auto-set status to online');
            setCurrentUserStatus('online');
            setCurrentUserCustomStatus(user.customStatus || '');
          }
        } catch (error) {
          console.error('Failed to auto-set online status:', error);
        }
      };
      
      autoSetOnline();
    }

    const token = localStorage.getItem('token');
    const socketURL = process.env.NODE_ENV === 'production' 
      ? window.location.origin
      : 'http://localhost:3000';
    
    const newSocket = io(socketURL, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('Chat Socket connected');
      setConnectionStatus('connected');
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Chat Socket disconnected:', reason);
      setConnectionStatus('disconnected');
    });

    newSocket.on('connect_error', () => {
      setConnectionStatus('error');
    });

    newSocket.on('reconnecting', () => {
      setConnectionStatus('reconnecting');
    });
    
    newSocket.on('room_message', ({ message, roomId }) => {
      if (currentRoom && roomId === currentRoom._id) {
        setMessages(prev => [...prev, message]);
        scrollToBottom();
      }
    });

    newSocket.on('message_updated', (updatedMessage) => {
      setMessages(prev => prev.map(msg => 
        msg._id === updatedMessage._id ? { ...msg, ...updatedMessage } : msg
      ));
    });

    newSocket.on('message_deleted', ({ messageId }) => {
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
    });

    newSocket.on('message_deleted_everyone', ({ messageId, roomId }) => {
      setMessages(prev => prev.map(msg => 
        msg._id === messageId ? { ...msg, deleteForEveryone: true, content: '[Message deleted]' } : msg
      ));
    });

    newSocket.on('user_typing_room', async ({ userId, roomId, isTyping, username }) => {
      if (currentRoom && roomId === currentRoom._id && userId !== user._id) {
        setTypingUsers(prev => {
          const newMap = new Map(prev);
          if (isTyping) {
            newMap.set(userId, username || 'Someone');
          } else {
            newMap.delete(userId);
          }
          return newMap;
        });
      }
    });

    newSocket.on('room_users_count', ({ roomId, count }) => {
      if (currentRoom && roomId === currentRoom._id) {
        setUserCount(count);
      }
    });

    newSocket.on('room_users_list', ({ roomId, users }) => {
      if (currentRoom && roomId === currentRoom._id) {
        setRoomUsers(users || []);
      }
    });

    // New socket events for enhanced features
    newSocket.on('message_edited', (editedMessage) => {
      setMessages(prev => prev.map(msg => 
        msg._id === editedMessage._id ? { ...msg, ...editedMessage } : msg
      ));
    });

    newSocket.on('thread_reply', ({ threadId, reply }) => {
      // Update thread reply count
      setMessages(prev => prev.map(msg => {
        if (msg._id === threadId) {
          return {
            ...msg,
            threadReplies: [...(msg.threadReplies || []), reply._id],
            threadReplyCount: (msg.threadReplyCount || 0) + 1
          };
        }
        return msg;
      }));
    });

    newSocket.on('user_status_changed', ({ userId, status, customStatus }) => {
      console.log('Socket user_status_changed event:', { userId, status, customStatus, currentUserId: user?._id });
      
      setUserStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, { status, customStatus });
        return newMap;
      });
      
      // Update current user's status if it's for them
      if (user && userId === user._id) {
        console.log('Updating current user status from socket event');
        setCurrentUserStatus(status);
        setCurrentUserCustomStatus(customStatus);
      }
      
      // Update user in room users list
      setRoomUsers(prev => prev.map(u => 
        u._id === userId ? { ...u, chatStatus: status, customStatus } : u
      ));
      
      // Update messages to reflect new status
      setMessages(prev => prev.map(msg => 
        msg.senderId?._id === userId 
          ? { ...msg, senderId: { ...msg.senderId, chatStatus: status, customStatus } }
          : msg
      ));
    });

    newSocket.on('message_read', ({ messageId, userId }) => {
      setMessages(prev => prev.map(msg => {
        if (msg._id === messageId) {
          const readBy = msg.readBy || [];
          if (!readBy.some(r => r.userId === userId)) {
            return { ...msg, readBy: [...readBy, { userId, readAt: new Date() }] };
          }
        }
        return msg;
      }));
    });

    setSocket(newSocket);

    return () => newSocket.close();
  }, [isAuthenticated, user, currentRoom]);

  // Join/Leave Room Logic
  useEffect(() => {
    if (!socket || !currentRoom) return;

    socket.emit('join_room', currentRoom._id);
    socket.emit('get_room_users', currentRoom._id);
    fetchMessages(currentRoom._id);
    setCurrentPage(1);
    setHasMoreMessages(true);

    return () => {
      socket.emit('leave_room', currentRoom._id);
      setMessages([]);
      setUserCount(0);
      setRoomUsers([]);
    };
  }, [socket, currentRoom]);

  const fetchMessages = async (roomId, page = 1) => {
    try {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);

      const response = await fetch(`/api/chat/rooms/${roomId}/messages?limit=50&page=${page}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        if (page === 1) {
          setMessages(data.data.messages);
          scrollToBottom();
        } else {
          setMessages(prev => [...data.data.messages, ...prev]);
        }
        setHasMoreMessages(data.data.currentPage < data.data.totalPages);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      showToast('Failed to load messages', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreMessages = () => {
    if (!loadingMore && hasMoreMessages && currentRoom) {
      fetchMessages(currentRoom._id, currentPage + 1);
    }
  };

  // Auto-load more messages when scrolling to top
  useEffect(() => {
    const messagesList = messagesListRef.current;
    if (!messagesList) return;

    const handleScroll = () => {
      if (messagesList.scrollTop === 0 && hasMoreMessages && !loadingMore) {
        const previousScrollHeight = messagesList.scrollHeight;
        loadMoreMessages();
        
        // Maintain scroll position after loading
        setTimeout(() => {
          const newScrollHeight = messagesList.scrollHeight;
          messagesList.scrollTop = newScrollHeight - previousScrollHeight;
        }, 100);
      }
    };

    messagesList.addEventListener('scroll', handleScroll);
    return () => messagesList.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, loadingMore, currentRoom, currentPage]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !currentRoom) return;

    const content = messageInput.trim();
    const replyToId = replyingTo ? replyingTo._id : null;

    try {
      setMessageInput('');
      setReplyingTo(null);
      setShowMentions(false);

      const response = await fetch(`/api/chat/rooms/${currentRoom._id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content, replyTo: replyToId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send');
      }
      scrollToBottom();
    } catch (error) {
      console.error('Send failed:', error);
      showToast(error.message || 'Failed to send message', 'error');
      setMessageInput(content);
    }
  };

  const handleTyping = (e) => {
    const value = e.target.value;
    setMessageInput(value);

    // Check for @ mentions
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1 && lastAtIndex === cursorPos - 1) {
      setShowMentions(true);
      setMentionSearch('');
      setSelectedMentionIndex(0);
      fetchRoomUsers();
    } else if (lastAtIndex !== -1 && textBeforeCursor.substring(lastAtIndex + 1).indexOf(' ') === -1) {
      const search = textBeforeCursor.substring(lastAtIndex + 1);
      setMentionSearch(search);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
    
    if (socket && currentRoom) {
      socket.emit('typing_room', { roomId: currentRoom._id, isTyping: true, username: user.username });
      
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('typing_room', { roomId: currentRoom._id, isTyping: false });
      }, 2000);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      const response = await fetch(`/api/chat/rooms/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        showToast('Message deleted', 'success');
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      showToast('Failed to delete message', 'error');
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    messageInputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      const response = await fetch(`/api/chat/rooms/messages/${messageId}/react`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ emoji })
      });
      if (!response.ok) throw new Error('Failed to react');
    } catch (error) {
      console.error('Reaction failed:', error);
      showToast('Failed to add reaction', 'error');
    }
  };

  const handlePin = async (messageId) => {
    try {
      const response = await fetch(`/api/chat/rooms/messages/${messageId}/pin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        showToast('Message pin toggled', 'success');
      } else {
        throw new Error('Failed to pin');
      }
    } catch (error) {
      console.error('Pin failed:', error);
      showToast('Failed to toggle pin', 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast('File size must be less than 5MB', 'error');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setMessageInput(prev => prev + ` ${data.url}`);
        showToast('File uploaded successfully', 'success');
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('Failed to upload file', 'error');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fetchRoomUsers = async () => {
    if (!currentRoom) return;
    // In a real implementation, you'd fetch active users in the room
    // For now, we'll use a placeholder
    setMentionUsers([]);
  };

  const insertMention = (username) => {
    const cursorPos = messageInputRef.current?.selectionStart || 0;
    const textBeforeCursor = messageInput.substring(0, cursorPos);
    const textAfterCursor = messageInput.substring(cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    const newText = 
      messageInput.substring(0, lastAtIndex) + 
      `@${username} ` + 
      textAfterCursor;
    
    setMessageInput(newText);
    setShowMentions(false);
    messageInputRef.current?.focus();
  };

  const handleMuteUser = async (userId, roomId) => {
    if (!window.confirm('Mute this user for 5 minutes?')) return;
    
    try {
      const response = await fetch(`/api/chat/rooms/${roomId}/mute/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ duration: 5 })
      });
      
      if (response.ok) {
        showToast('User muted for 5 minutes', 'success');
      } else {
        throw new Error('Failed to mute');
      }
    } catch (error) {
      console.error('Mute failed:', error);
      showToast('Failed to mute user', 'error');
    }
  };

  const handleBanUser = async (userId, roomId) => {
    if (!window.confirm('Ban this user from this room? This action requires admin privileges.')) return;
    
    try {
      const response = await fetch(`/api/chat/rooms/${roomId}/ban/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        showToast('User banned from room', 'success');
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to ban');
      }
    } catch (error) {
      console.error('Ban failed:', error);
      showToast(error.message || 'Failed to ban user', 'error');
    }
  };

  // ========== NEW FEATURE HANDLERS ==========
  
  const handleEditMessage = async (messageId, newContent) => {
    try {
      const response = await fetch(`/api/chat/rooms/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content: newContent })
      });

      if (response.ok) {
        showToast('Message edited', 'success');
      } else {
        throw new Error('Failed to edit');
      }
    } catch (error) {
      console.error('Edit failed:', error);
      showToast('Failed to edit message', 'error');
    }
  };

  const handleDeleteForEveryone = async (messageId) => {
    if (!window.confirm('Delete this message for everyone? This cannot be undone.')) return;
    
    try {
      const response = await fetch(`/api/chat/rooms/messages/${messageId}/everyone`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        showToast('Message deleted for everyone', 'success');
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      showToast('Failed to delete message', 'error');
    }
  };

  const handleBookmark = async (messageId, note = '', tags = []) => {
    try {
      const response = await fetch(`/api/chat/rooms/messages/${messageId}/bookmark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ note, tags })
      });

      if (response.ok) {
        showToast('Message bookmarked', 'success');
        // Refresh bookmarks if panel is open
        if (showBookmarksPanel) {
          fetchBookmarks();
        }
      } else {
        throw new Error('Failed to bookmark');
      }
    } catch (error) {
      console.error('Bookmark failed:', error);
      showToast('Failed to bookmark message', 'error');
    }
  };

  const handleUnbookmark = async (messageId) => {
    try {
      const response = await fetch(`/api/chat/rooms/messages/${messageId}/bookmark`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        showToast('Bookmark removed', 'success');
        fetchBookmarks();
      }
    } catch (error) {
      console.error('Unbookmark failed:', error);
      showToast('Failed to remove bookmark', 'error');
    }
  };

  const fetchBookmarks = async () => {
    try {
      const response = await fetch('/api/chat/rooms/bookmarks', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setBookmarkedMessages(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    }
  };

  const handleOpenThread = (message) => {
    setCurrentThread(message);
    setShowThreadView(true);
  };

  const handleThreadReply = async (content, parentMessageId) => {
    try {
      const response = await fetch(`/api/chat/rooms/messages/${parentMessageId}/thread`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content })
      });

      if (response.ok) {
        showToast('Reply sent', 'success');
        // Refresh thread view
        if (currentThread) {
          // Thread will be updated via socket
        }
      } else {
        throw new Error('Failed to send reply');
      }
    } catch (error) {
      console.error('Thread reply failed:', error);
      showToast('Failed to send reply', 'error');
    }
  };

  const handleViewUserProfile = async (userId) => {
    try {
      // First try to find user in current data to get username
      let targetUser = roomUsers.find(u => u._id === userId);
      if (!targetUser) {
        const message = messages.find(m => m.senderId?._id === userId);
        targetUser = message?.senderId;
      }
      
      // Fetch full profile from API using username
      if (targetUser?.username) {
        const response = await fetch(`/api/profile/${targetUser.username}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          // Add any missing fields from local data
          const completeUserData = {
            ...userData,
            chatStatus: targetUser.chatStatus || userData.chatStatus,
            customStatus: targetUser.customStatus || userData.customStatus,
            activityPoints: userData.activityPoints || { total: 0, current: 0 },
            followers: userData.followers || [],
            following: userData.following || []
          };
          setSelectedUserProfile(completeUserData);
        } else {
          // Fallback to local data if API fails
          if (targetUser) {
            setSelectedUserProfile(targetUser);
          } else {
            showToast('Could not load user profile', 'error');
          }
        }
      } else if (targetUser) {
        // Use local data if no username available
        setSelectedUserProfile(targetUser);
      } else {
        showToast('Could not load user profile', 'error');
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      // Try to use cached data as fallback
      const targetUser = roomUsers.find(u => u._id === userId) || messages.find(m => m.senderId?._id === userId)?.senderId;
      if (targetUser) {
        setSelectedUserProfile(targetUser);
      } else {
        showToast('Could not load user profile', 'error');
      }
    }
  };

  const handleStatusChange = async (newStatus, customStatus) => {
    console.log('handleStatusChange called with:', { newStatus, customStatus });
    try {
      const response = await fetch('/api/chat/rooms/status', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus, customStatus })
      });

      if (response.ok) {
        console.log('Status updated successfully, setting local state');
        showToast('Status updated', 'success');
        setShowStatusSelector(false);
        // Update local state immediately
        setCurrentUserStatus(newStatus);
        setCurrentUserCustomStatus(customStatus);
        console.log('Local status state updated to:', { newStatus, customStatus });
        // Socket will broadcast the change automatically from server
      } else {
        const error = await response.json();
        showToast(error.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Status change failed:', error);
      showToast('Failed to update status', 'error');
    }
  };

  const handleRequestDM = (targetUser) => {
    if (socket) {
      socket.emit('request_dm', { targetUserId: targetUser._id });
      showToast(`DM request sent to ${targetUser.username}`, 'success');
    }
  };

  const handleFileUploadNew = (fileData) => {
    // Send message with file attachment
    if (socket && currentRoom) {
      socket.emit('room_message', {
        roomId: currentRoom._id,
        content: fileData.caption || 'Sent a file',
        attachments: [fileData]
      });
      showToast('File sent', 'success');
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await fetch(`/api/chat/rooms/messages/${messageId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      // Socket will update the UI
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Mark visible messages as read when they appear
  useEffect(() => {
    if (!messages.length || !user) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const messageId = entry.target.getAttribute('data-message-id');
          const message = messages.find(m => m._id === messageId);
          if (message && message.senderId?._id !== user._id) {
            const isRead = message.readBy?.some(r => r.userId === user._id);
            if (!isRead) {
              handleMarkAsRead(messageId);
            }
          }
        }
      });
    }, { threshold: 0.5 });

    // Observe all message elements
    const messageElements = document.querySelectorAll('[data-message-id]');
    messageElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [messages, user]);

  // ========== END NEW FEATURE HANDLERS ==========

  if (!isAuthenticated) return <div className="chat-container">Please login to chat.</div>;
  if (loading) return <div className="chat-container">Loading chat...</div>;

  return (
    <div className="chat-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="chat-header">
        <h1>💬 Public Chat Rooms</h1>
        {/* Connection Status */}
        <div className={`connection-status status-${connectionStatus}`}>
          {connectionStatus === 'connected' && '🟢 Connected'}
          {connectionStatus === 'connecting' && '🟡 Connecting...'}
          {connectionStatus === 'disconnected' && '🔴 Disconnected'}
          {connectionStatus === 'reconnecting' && '🟡 Reconnecting...'}
          {connectionStatus === 'error' && '🔴 Connection Error'}
        </div>
      </div>

      <div className="chat-layout">
        {/* Sidebar */}
        <div className={`chat-sidebar ${showSidebar ? 'active' : ''}`}>
          <div className="chat-sidebar-header">
            Active Rooms
          </div>
          {/* Room Search */}
          <div className="room-search-container">
            <input
              type="text"
              className="room-search-input"
              placeholder="Search rooms..."
              value={roomSearchQuery}
              onChange={(e) => setRoomSearchQuery(e.target.value)}
            />
            {roomSearchQuery && (
              <button 
                className="clear-search-btn"
                onClick={() => setRoomSearchQuery('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <div className="room-list">
            {(() => {
              // Filter rooms based on search
              const filteredRooms = rooms.filter(room => 
                room.name.toLowerCase().includes(roomSearchQuery.toLowerCase()) ||
                room.description.toLowerCase().includes(roomSearchQuery.toLowerCase())
              );

              if (filteredRooms.length === 0) {
                return (
                  <div className="no-rooms-found">
                    No rooms found matching "{roomSearchQuery}"
                  </div>
                );
              }

              // Group rooms by category
              const roomsByCategory = filteredRooms.reduce((acc, room) => {
                const category = room.category || 'General';
                if (!acc[category]) {
                  acc[category] = [];
                }
                acc[category].push(room);
                return acc;
              }, {});

              // Sort categories by the categoryOrder of their first room
              const sortedCategories = Object.keys(roomsByCategory).sort((a, b) => {
                const orderA = roomsByCategory[a][0]?.categoryOrder || 0;
                const orderB = roomsByCategory[b][0]?.categoryOrder || 0;
                return orderA - orderB;
              });

              const toggleCategory = (category) => {
                setCollapsedCategories(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(category)) {
                    newSet.delete(category);
                  } else {
                    newSet.add(category);
                  }
                  return newSet;
                });
              };

              return sortedCategories.map(category => {
                const isCollapsed = collapsedCategories.has(category);
                
                return (
                  <div key={category} className="room-category">
                    <div 
                      className="category-header" 
                      onClick={() => toggleCategory(category)}
                    >
                      <span className={`category-arrow ${isCollapsed ? 'collapsed' : ''}`}>▼</span>
                      <span className="category-name">{category.toUpperCase()}</span>
                    </div>
                    {!isCollapsed && roomsByCategory[category].map(room => (
                    <div 
                      key={room._id} 
                      className={`room-item ${currentRoom?._id === room._id ? 'active' : ''} ${room.type === 'private' ? 'private-room' : ''}`}
                      onClick={() => {
                        setCurrentRoom(room);
                        setShowSidebar(false);
                      }}
                    >
                      <span className="room-icon">
                        {room.type === 'private' ? '🔒' : '#'}
                      </span>
                      <div className="room-info">
                        <div className="room-name">
                          {room.name}
                          {room.type === 'private' && <span className="private-badge">Private</span>}
                        </div>
                        <div className="room-desc">{room.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            });
            })()}
          </div>
        </div>

        {/* Main Chat */}
        <div className="chat-main">
          {currentRoom ? (
            <>
              <div className="chat-room-header">
                <div className="chat-room-title">
                  <h2>#{currentRoom.name} <span className="online-count">({userCount} online)</span></h2>
                  <p>{currentRoom.description}</p>
                </div>
                
                {/* Chat Toolbar */}
                <div className="chat-toolbar">
                  <button 
                    className="toolbar-action-btn" 
                    onClick={() => setShowSearchModal(true)}
                    title="Search messages"
                  >
                    🔍
                  </button>
                  <button 
                    className="toolbar-action-btn" 
                    onClick={() => {
                      setShowBookmarksPanel(true);
                      fetchBookmarks();
                    }}
                    title="View bookmarks"
                  >
                    📑
                  </button>
                  <button 
                    className="toolbar-action-btn" 
                    onClick={() => setShowStatusSelector(true)}
                    title="Set status"
                  >
                    🟢
                  </button>
                  <button 
                    className="toolbar-action-btn" 
                    onClick={async () => {
                      try {
                        const response = await fetch(`/api/chat/rooms/export?roomId=${currentRoom._id}&format=txt`, {
                          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                        });
                        
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `chat-export-${currentRoom.slug}-${Date.now()}.txt`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                          showToast('Chat exported successfully', 'success');
                        } else {
                          const data = await response.json();
                          showToast(data.message || 'Failed to export chat', 'error');
                        }
                      } catch (error) {
                        console.error('Export error:', error);
                        showToast('Failed to export chat', 'error');
                      }
                    }}
                    title="Export chat history"
                  >
                    💾
                  </button>
                </div>

                {/* Mobile Toggle */}
                <button 
                  className="btn-icon mobile-only" 
                  onClick={() => setShowSidebar(!showSidebar)}
                  style={{ display: window.innerWidth > 768 ? 'none' : 'block' }}
                >
                  ☰
                </button>
              </div>

              <div className="messages-list" ref={messagesListRef}>
                {/* Auto-loading indicator */}
                {loadingMore && (
                  <div className="loading-more-indicator">
                    <div className="loading-spinner"></div>
                    <span>Loading older messages...</span>
                  </div>
                )}

                {messages.map((msg, index) => {
                  const showHeader = index === 0 || messages[index - 1].senderId._id !== msg.senderId._id;
                  return (
                    <MessageItem
                      key={msg._id}
                      message={msg}
                      currentUser={user}
                      showHeader={showHeader}
                      onEdit={handleEditMessage}
                      onDelete={handleDeleteMessage}
                      onDeleteForEveryone={handleDeleteForEveryone}
                      onReply={handleReply}
                      onReact={handleReaction}
                      onPin={handlePin}
                      onBookmark={handleBookmark}
                      onOpenThread={handleOpenThread}
                      onViewProfile={handleViewUserProfile}
                      onMute={handleMuteUser}
                      onBan={handleBanUser}
                      currentRoom={currentRoom}
                    />
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Typing Indicator */}
              {typingUsers.size > 0 && (
                <div className="typing-indicator">
                  <span className="typing-dots">
                    {Array.from(typingUsers.values()).join(', ')} {typingUsers.size === 1 ? 'is' : 'are'} typing
                    <span className="dot">.</span><span className="dot">.</span><span className="dot">.</span>
                  </span>
                </div>
              )}

              <div className="chat-input-area">
                <MessageInput
                  onSend={async (content, threadId) => {
                    if (threadId) {
                      await handleThreadReply(content, threadId);
                    } else {
                      // Send regular message
                      try {
                        const response = await fetch(`/api/chat/rooms/${currentRoom._id}/messages`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                          },
                          body: JSON.stringify({ 
                            content, 
                            replyTo: replyingTo ? replyingTo._id : null 
                          })
                        });

                        if (response.ok) {
                          setReplyingTo(null);
                          scrollToBottom();
                        } else {
                          const error = await response.json();
                          showToast(error.message || 'Failed to send message', 'error');
                        }
                      } catch (error) {
                        console.error('Send failed:', error);
                        showToast('Failed to send message', 'error');
                      }
                    }
                  }}
                  onFileUpload={handleFileUploadNew}
                  replyingTo={replyingTo}
                  onCancelReply={cancelReply}
                  threadId={null}
                  placeholder={`Message #${currentRoom.name}`}
                  currentRoom={currentRoom}
                />
              </div>
            </>
          ) : (
            <div className="no-room-selected">
              Select a room to start chatting
            </div>
          )}
        </div>

        {/* Users Sidebar */}
        <div className="chat-users-sidebar">
          <div className="chat-sidebar-header">
            Online in Room ({roomUsers.filter(u => u.chatStatus && u.chatStatus !== 'offline').length})
          </div>
          <div className="user-list">
            {(() => {
              // Helper function to get status icon
              const getStatusIcon = (status) => {
                switch(status) {
                  case 'online': return '🟢';
                  case 'away': return '🟡';
                  case 'busy': return '🔴';
                  case 'dnd': return '⛔';
                  case 'invisible': return '⚫';
                  default: return '⚫'; // offline
                }
              };

              // Helper function to get status label
              const getStatusLabel = (status) => {
                switch(status) {
                  case 'online': return 'Online';
                  case 'away': return 'Away';
                  case 'busy': return 'Busy';
                  case 'dnd': return 'Do Not Disturb';
                  case 'invisible': return 'Invisible';
                  default: return 'Offline';
                }
              };

              // Cosmetic helper functions
              const getAvatarFrameClass = (roomUser) => {
                if (!roomUser.equippedCosmetics?.avatarFrame) return '';
                const frameId = roomUser.equippedCosmetics.avatarFrame;
                const frameMap = {
                  'frame-neon-purple': 'neon-purple-frame',
                  'frame-gold-elite': 'gold-elite-frame',
                  'frame-rgb-animated': 'rgb-animated-frame',
                  'frame-fire': 'fire-frame',
                  'frame-ice': 'ice-frame'
                };
                return frameMap[frameId] || '';
              };

              const getUsernameEffectClass = (roomUser) => {
                if (!roomUser.equippedCosmetics?.usernameEffect) return '';
                // Return the ID directly as it matches the CSS class name
                return roomUser.equippedCosmetics.usernameEffect;
              };

              const getBadgeIcons = (roomUser) => {
                if (!roomUser.equippedCosmetics?.badges || roomUser.equippedCosmetics.badges.length === 0) return null;
                const badgeMap = {
                  'badge-og-member': '🏆',
                  'badge-giveaway-legend': '🎁',
                  'badge-social-star': '⭐',
                  'badge-collector': '💎',
                  'badge-game-master': '🎮'
                };
                return roomUser.equippedCosmetics.badges.map(badgeId => badgeMap[badgeId] || '').filter(Boolean);
              };

              // Render a user item
              const renderUser = (roomUser) => (
                <div key={roomUser._id} className="user-item" onClick={() => setSelectedUserProfile(roomUser)}>
                  {roomUser.profile?.avatar?.url ? (
                    <img 
                      src={roomUser.profile.avatar.url} 
                      alt={roomUser.username}
                      className={`user-item-avatar ${getAvatarFrameClass(roomUser)}`}
                    />
                  ) : (
                    <div className={`user-item-avatar avatar-placeholder ${getAvatarFrameClass(roomUser)}`}>
                      {roomUser.username[0].toUpperCase()}
                    </div>
                  )}
                  <div className="user-info">
                    <div className="user-name-row">
                      <span className={`user-name ${getUsernameEffectClass(roomUser)}`}>
                        {roomUser.username}
                        {getBadgeIcons(roomUser) && (
                          <span className="cosmetic-badges-chat">
                            {getBadgeIcons(roomUser).map((icon, idx) => (
                              <span key={idx} className="cosmetic-badge-inline">{icon}</span>
                            ))}
                          </span>
                        )}
                        {roomUser.isAdmin && <span className="user-badge admin">👑</span>}
                        {roomUser.accountType === 'moderator' && <span className="user-badge mod">🛡️</span>}
                      </span>
                    </div>
                    {roomUser.customStatus && (
                      <div className="user-custom-status" title={roomUser.customStatus}>
                        {roomUser.customStatus}
                      </div>
                    )}
                  </div>
                </div>
              );

              // Show all users (including offline), grouped by status
              if (roomUsers.length === 0) {
                return (
                  <div className="user-item">
                    <span className="user-name" style={{opacity: 0.5}}>No users</span>
                  </div>
                );
              }

              // Group users by status (including offline and invisible)
              const statusGroups = {
                online: roomUsers.filter(u => u.chatStatus === 'online'),
                away: roomUsers.filter(u => u.chatStatus === 'away'),
                busy: roomUsers.filter(u => u.chatStatus === 'busy'),
                dnd: roomUsers.filter(u => u.chatStatus === 'dnd'),
                invisible: roomUsers.filter(u => u.chatStatus === 'invisible'),
                offline: roomUsers.filter(u => !u.chatStatus || u.chatStatus === 'offline')
              };

              return (
                <>
                  {Object.entries(statusGroups).map(([status, users]) => {
                    if (users.length === 0) return null;
                    
                    return (
                      <div key={status} className="user-status-group">
                        <div className="user-status-header">
                          <span className="status-icon">{getStatusIcon(status)}</span>
                          <span className="status-label">{getStatusLabel(status)}</span>
                          <span className="status-count">— {users.length}</span>
                        </div>
                        <div className="user-status-list">
                          {users.map(renderUser)}
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Modal Components */}
      {showThreadView && currentThread && (
        <ThreadView
          parentMessage={currentThread}
          onClose={() => setShowThreadView(false)}
          onReply={handleThreadReply}
          currentUser={user}
        />
      )}

      {showSearchModal && (
        <MessageSearch
          onClose={() => setShowSearchModal(false)}
          onSelectMessage={(msg) => {
            // Scroll to message or load room with that message
            setShowSearchModal(false);
            if (msg.roomId !== currentRoom._id) {
              const targetRoom = rooms.find(r => r._id === msg.roomId);
              if (targetRoom) setCurrentRoom(targetRoom);
            }
            showToast('Message found', 'success');
          }}
          currentUser={user}
          rooms={rooms}
        />
      )}

      {showBookmarksPanel && (
        <BookmarksPanel
          bookmarks={bookmarkedMessages}
          onClose={() => setShowBookmarksPanel(false)}
          onRemove={handleUnbookmark}
          onSelectMessage={(msg) => {
            // Navigate to message
            setShowBookmarksPanel(false);
            if (msg.roomId !== currentRoom._id) {
              const targetRoom = rooms.find(r => r._id === msg.roomId);
              if (targetRoom) setCurrentRoom(targetRoom);
            }
          }}
        />
      )}

      {selectedUserProfile && (
        <UserProfileCard
          user={selectedUserProfile}
          onClose={() => setSelectedUserProfile(null)}
          onDM={handleRequestDM}
          onBlock={async (targetUser) => {
            try {
              const response = await fetch(`/api/chat/rooms/block/${targetUser._id}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ reason: 'Blocked from chat' })
              });
              if (response.ok) {
                showToast('User blocked', 'success');
                setSelectedUserProfile(null);
              }
            } catch (error) {
              showToast('Failed to block user', 'error');
            }
          }}
          currentUser={user}
        />
      )}

      {showStatusSelector && (
        <StatusSelector
          currentStatus={currentUserStatus}
          currentCustomStatus={currentUserCustomStatus}
          onStatusChange={handleStatusChange}
          onClose={() => setShowStatusSelector(false)}
        />
      )}
    </div>
  );
};

export default Chat;
